import React, { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useAnnoScolastico } from "@/lib/annoScolastico";
import AnnoSelector from "@/components/AnnoSelector";

/**
 * Pagina "Cedole" — gestione richieste cedole librarie ai Comuni.
 *
 * Workflow:
 * 1. Lista comuni di residenza degli alunni Primaria (E + F) con conteggio.
 * 2. Bottone 👁 → popup elenco alunni del comune ordinati per settore + classe.
 * 3. Bottone 🖨 → genera PDF lettera richiesta (da protocollare manualmente).
 * 4. Bottone ✉ → invia mail al Comune con allegato manuale (lettera firmata).
 * 5. Bottone ✏ → modifica anagrafica Comune (email/pec/note).
 *
 * Richiesta utente 2026-02-17.
 */

export default function CedolePage() {
  const { annoCorrente } = useAuth();
  // 🆕 Feb 2026: usa il context globale AS (persistito localStorage, sincronizzato
  // con il selettore in alto a destra). Fallback su annoCorrente da AuthContext.
  const { anno: annoGlobal } = useAnnoScolastico();
  const annoAttivo = annoGlobal ?? annoCorrente;
  const [data, setData] = useState({ items: [], total: 0, anno: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [showAlunniModal, setShowAlunniModal] = useState(null); // {comune}
  const [showEmailModal, setShowEmailModal] = useState(null);   // {comune}
  const [showAnagModal, setShowAnagModal] = useState(null);     // {comune}
  const [showLogModal, setShowLogModal] = useState(null);       // null | {comune?}
  const [showNoteModal, setShowNoteModal] = useState(null);     // null | comune item
  const [sortMode, setSortMode] = useState("nome");             // "nome" (A→Z) | "count" (più alunni prima)

  const load = async () => {
    if (!annoAttivo) return;
    setLoading(true); setErr("");
    try {
      const r = await api.get("/cedole/comuni", { params: { anno: annoAttivo } });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [annoAttivo]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    let arr = q ? data.items.filter(it => (it.nome || "").includes(q)) : data.items;
    arr = [...arr];
    if (sortMode === "nome") {
      arr.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "it", { sensitivity: "base" }));
    } else {
      // count desc, poi nome asc (default originale del backend)
      arr.sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0) || String(a.nome || "").localeCompare(String(b.nome || ""), "it"));
    }
    return arr;
  }, [data.items, search, sortMode]);

  const totAlunni = data.items.reduce((s, it) => s + (it.count || 0), 0);

  const onPdf = (it) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const annoQ = annoAttivo ? `?anno=${annoAttivo}` : "";
    window.open(`${base}/api/cedole/lettera/${encodeURIComponent(it.nome_norm)}/pdf${annoQ}`,
      "_blank", "noopener,noreferrer");
  };
  const onXlsx = (it) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const annoQ = annoAttivo ? `?anno=${annoAttivo}` : "";
    const a = document.createElement("a");
    a.href = `${base}/api/cedole/lettera/${encodeURIComponent(it.nome_norm)}/xlsx${annoQ}`;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const onSetModalita = async (it, next) => {
    if ((it.modalita || "nulla") === next) return;
    try {
      await api.put(`/cedole/comuni/${encodeURIComponent(it.nome_norm)}`,
        { nome: it.nome, email: it.email, pec: it.pec, indirizzo: it.indirizzo,
          telefono: it.telefono, note: it.note, modalita: next,
          avanzamento: it.avanzamento || "da_fare" });
      setData(d => ({ ...d, items: d.items.map(x => x.nome_norm === it.nome_norm ? { ...x, modalita: next } : x) }));
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const onSetAvanzamento = async (it, next) => {
    if ((it.avanzamento || "da_fare") === next) return;
    try {
      await api.put(`/cedole/comuni/${encodeURIComponent(it.nome_norm)}`,
        { nome: it.nome, email: it.email, pec: it.pec, indirizzo: it.indirizzo,
          telefono: it.telefono, note: it.note,
          modalita: it.modalita || "nulla", avanzamento: next });
      setData(d => ({ ...d, items: d.items.map(x => x.nome_norm === it.nome_norm ? { ...x, avanzamento: next } : x) }));
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="cedole-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>📚 Richiesta Cedole Comuni</h1>
        <div className="sw-breadcrumb">Home &gt; Richiesta Cedole Comuni</div>
        <button onClick={() => setShowLogModal({})} data-testid="cedole-log-all-btn"
          title="Storico generale invii mail Cedole"
          style={{ marginLeft: "auto", padding: "7px 14px", background: "#37474f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          📜 Storico invii
        </button>
      </div>

      <div className="sw-card">
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <AnnoSelector testId="cedole-anno-attivo" />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>🔍 Cerca comune:</span>
            <input type="text" placeholder="nome comune…" value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="cedole-search"
              className="sw-filter-input" style={{ width: 240 }} />
            {search && <button className="sw-filter-btn" onClick={() => setSearch("")}>Pulisci</button>}
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>🔢 Ordina:</span>
            <select value={sortMode} onChange={e => setSortMode(e.target.value)}
              data-testid="cedole-sort"
              className="sw-filter-input" style={{ width: 180 }}>
              <option value="nome">📋 Per Comune (A→Z)</option>
              <option value="count">👥 Più alunni prima</option>
            </select>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}>
              <b>{filtered.length}</b> / {data.total} comuni · <b style={{ color: "#e65100" }}>{totAlunni}</b> alunni Primaria (E + F)
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#555", lineHeight: 1.4 }}>
            Lista dei Comuni di residenza degli alunni di Primaria (settori E + F).
            <b> 1)</b> 🖨 Stampa la lettera in PDF — la firmi/protocolli con altro software.
            <b> 2)</b> ✉ Invia la mail al Comune allegando manualmente la lettera firmata e protocollata.
          </div>
        </div>
      </div>

      {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", margin: "8px 0", fontSize: 12 }}>{err}</div>}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={{ overflow: "auto", maxHeight: "75vh" }}>
          <table className="sw-table" style={{ width: "100%" }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 40, textAlign: "center" }}>#</th>
                <th>Comune</th>
                <th style={{ width: 70, textAlign: "center" }}>Alunni</th>
                <th style={{ width: 70, textAlign: "center" }}>Sett. E</th>
                <th style={{ width: 70, textAlign: "center" }}>Sett. F</th>
                <th style={{ minWidth: 180 }}>Email</th>
                <th style={{ width: 110, textAlign: "center" }}>Modalità</th>
                <th style={{ width: 130, textAlign: "center" }} title="Stato di avanzamento richiesta cedole">Avanzamento</th>
                <th style={{ width: 60, textAlign: "center" }}>Note</th>
                <th style={{ width: 340, textAlign: "center" }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="sw-loading">Caricamento…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="sw-empty"><p>Nessun comune trovato</p></td></tr>
              ) : filtered.map((it, i) => {
                const modalita = it.modalita || "nulla";
                const avanzamento = it.avanzamento || "da_fare";
                const modBg = modalita === "digitale" ? "#0277bd"
                            : modalita === "cartacea" ? "#6d4c41"
                            : modalita === "codice" ? "#00897b" : "#9e9e9e";
                const avzBg = avanzamento === "ricevuto_cedole" ? "#1565c0"
                            : avanzamento === "inviata_documentazione" ? "#2e7d32" : "#ef6c00";
                const hasNote = !!(it.note && it.note.trim());
                return (
                <tr key={it.nome_norm} data-testid={`cedole-row-${it.nome_norm}`}>
                  <td style={{ color: "#888", textAlign: "center", fontSize: 11 }}>{i + 1}</td>
                  <td style={{ fontWeight: 700, color: "#0d47a1" }}>{it.nome}</td>
                  <td style={{ textAlign: "center", fontWeight: 700, color: "#e65100", fontSize: 14 }}>{it.count}</td>
                  <td style={{ textAlign: "center" }}>{it.by_settore?.E || ""}</td>
                  <td style={{ textAlign: "center" }}>{it.by_settore?.F || ""}</td>
                  <td style={{ fontSize: 11, fontFamily: "Consolas, monospace", color: it.email ? "#1b5e20" : "#999" }}>
                    {it.email || <span style={{ fontStyle: "italic" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <select
                      value={modalita}
                      onChange={(e) => onSetModalita(it, e.target.value)}
                      data-testid={`cedole-modalita-${it.nome_norm}`}
                      title="Modalità invio"
                      style={{
                        padding: "3px 8px",
                        background: modBg,
                        color: "white", border: "none", fontSize: 11, fontWeight: 800,
                        cursor: "pointer", letterSpacing: 0.3, borderRadius: 10,
                        appearance: "auto",
                      }}>
                      <option value="nulla">— Nulla —</option>
                      <option value="digitale">💻 Digitale</option>
                      <option value="cartacea">📄 Cartacea</option>
                      <option value="codice">📟 Codice</option>
                    </select>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <select
                      value={avanzamento}
                      onChange={(e) => onSetAvanzamento(it, e.target.value)}
                      data-testid={`cedole-avanzamento-${it.nome_norm}`}
                      title="Stato avanzamento"
                      style={{
                        padding: "3px 8px",
                        background: avzBg,
                        color: "white", border: "none", fontSize: 11, fontWeight: 800,
                        cursor: "pointer", letterSpacing: 0.3, borderRadius: 10,
                        appearance: "auto",
                      }}>
                      <option value="da_fare">⏳ Da fare</option>
                      <option value="inviata_documentazione">✅ Inviata documentazione</option>
                      <option value="ricevuto_cedole">📬 Ricevuto cedole</option>
                    </select>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button onClick={() => setShowNoteModal(it)}
                      data-testid={`cedole-note-${it.nome_norm}`}
                      title={hasNote ? `Note salvate:\n${it.note}` : "Aggiungi note"}
                      style={{
                        padding: "4px 8px",
                        background: hasNote ? "#f57f17" : "#90a4ae",
                        color: "white", border: "none", fontSize: 13, fontWeight: 700,
                        cursor: "pointer", borderRadius: 3, position: "relative",
                      }}>
                      📝
                      {hasNote && (
                        <span style={{
                          position: "absolute", top: -4, right: -4,
                          width: 8, height: 8, borderRadius: "50%", background: "#c62828",
                        }} />
                      )}
                    </button>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button onClick={() => setShowAlunniModal({ comune: it })}
                      data-testid={`cedole-vedi-${it.nome_norm}`}
                      title="Vedi elenco alunni"
                      style={{ padding: "4px 8px", background: "#37474f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                      👁
                    </button>
                    <button onClick={() => onPdf(it)}
                      data-testid={`cedole-pdf-${it.nome_norm}`}
                      title="Genera PDF lettera richiesta cedole"
                      style={{ padding: "4px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                      🖨 PDF
                    </button>
                    <button onClick={() => onXlsx(it)}
                      data-testid={`cedole-xlsx-${it.nome_norm}`}
                      title="Scarica Excel elenco alunni del comune"
                      style={{ padding: "4px 8px", background: "#2e7d32", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                      📊 Excel
                    </button>
                    <button onClick={() => setShowEmailModal({ comune: it })}
                      data-testid={`cedole-email-${it.nome_norm}`}
                      title="Invia email con allegati (lettera firmata + lista libri ecc.)"
                      style={{ padding: "4px 8px", background: "#558b2f", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                      ✉ Email
                    </button>
                    <button onClick={() => setShowAnagModal({ comune: it })}
                      data-testid={`cedole-anag-${it.nome_norm}`}
                      title="Modifica anagrafica comune"
                      style={{ padding: "4px 8px", background: "#5d4037", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                      ✏
                    </button>
                    <button onClick={() => setShowLogModal({ comune: it })}
                      data-testid={`cedole-log-${it.nome_norm}`}
                      title="Storico invii mail per questo comune"
                      style={{ padding: "4px 8px", background: "#455a64", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                      📜
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAlunniModal && <AlunniModal data={showAlunniModal} annoCorrente={annoAttivo} onClose={() => setShowAlunniModal(null)} />}
      {showEmailModal && <EmailModal data={showEmailModal} annoCorrente={annoAttivo} onClose={() => setShowEmailModal(null)} onSent={load} />}
      {showAnagModal && <AnagModal data={showAnagModal} onClose={() => setShowAnagModal(null)} onSaved={load} />}
      {showLogModal && <LogModal data={showLogModal} annoCorrente={annoAttivo} onClose={() => setShowLogModal(null)} />}
      {showNoteModal && <NoteModal data={showNoteModal} onClose={() => setShowNoteModal(null)}
        onSaved={(note) => {
          setData(d => ({ ...d, items: d.items.map(x => x.nome_norm === showNoteModal.nome_norm ? { ...x, note } : x) }));
          setShowNoteModal(null);
        }} />}
    </div>
  );
}

function LogModal({ data, annoCorrente, onClose }) {
  const c = data.comune;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const params = {};
    if (c?.nome_norm) params.comune = c.nome_norm;
    if (annoCorrente) params.anno = annoCorrente;
    api.get("/cedole/log", { params })
      .then(r => setList(r.data?.items || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [c, annoCorrente]);
  const fmtDt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <div onClick={onClose} data-testid="cedole-log-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(1000px, 95vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", border: "2px solid #37474f" }}>
        <div style={{ background: "#37474f", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase" }}>Storico invii mail Cedole</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>📜 {c ? c.nome : "Tutti i comuni"} — {list.length} invii</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
          {loading ? <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento…</div>
            : list.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun invio registrato.</div>
            : (
              <table className="sw-table" style={{ fontSize: 11 }}>
                <thead className="thead-blue"><tr>
                  <th style={{ width: 110 }}>Data/Ora</th>
                  {!c && <th>Comune</th>}
                  <th>Destinatario</th>
                  <th>Oggetto</th>
                  <th>Allegati</th>
                  <th style={{ width: 100 }}>Mittente</th>
                  <th style={{ width: 80 }}>Inviato da</th>
                </tr></thead>
                <tbody>
                  {list.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: "Consolas", fontSize: 10, color: "#37474f" }}>{fmtDt(r.sent_at)}</td>
                      {!c && <td style={{ fontWeight: 700, color: "#0d47a1" }}>{r.nome}</td>}
                      <td style={{ fontSize: 10 }}>{(r.destinatario || []).join(", ")}{r.cc?.length ? <span style={{ color: "#888" }}> · cc: {r.cc.join(", ")}</span> : null}</td>
                      <td style={{ fontSize: 10 }}>{r.oggetto}</td>
                      <td style={{ fontSize: 10 }}>
                        {(r.allegati || []).length === 0
                          ? <span style={{ color: "#999" }}>—</span>
                          : (r.allegati || []).map((a, ai) => (
                            <div key={ai} style={{ color: "#1b5e20" }}>📎 {a.filename} <span style={{ color: "#888" }}>({Math.round(a.size / 1024)} KB)</span></div>
                          ))
                        }
                      </td>
                      <td style={{ fontSize: 9, fontFamily: "Consolas", color: r.provider === "pec_legalmail" ? "#4a148c" : "#37474f" }}>
                        {r.provider === "pec_legalmail" ? "🔒 PEC" : (r.provider || "—")}<br/>
                        <span style={{ fontSize: 8 }}>{r.provider_from || ""}</span>
                      </td>
                      <td style={{ fontSize: 10 }}>{r.sent_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

function AlunniModal({ data, annoCorrente, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/cedole/comuni/${encodeURIComponent(data.comune.nome_norm)}`, { params: { anno: annoCorrente } })
      .then(r => setList(r.data?.alunni || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [data.comune.nome_norm, annoCorrente]);
  return (
    <div onClick={onClose} data-testid="cedole-alunni-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(900px, 95vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", border: "2px solid #37474f" }}>
        <div style={{ background: "#37474f", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase" }}>Alunni residenti — Primaria E + F</div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>📍 {data.comune.nome}</div>
            <div style={{ fontSize: 11, opacity: 0.9 }}>A.S. {annoCorrente}/{annoCorrente + 1} · {list.length} alunni</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
          {loading ? <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento…</div> : (
            <table className="sw-table" style={{ fontSize: 12 }}>
              <thead className="thead-blue"><tr>
                <th style={{ width: 30 }}>#</th>
                <th style={{ width: 50 }}>Sett.</th>
                <th style={{ width: 60 }}>Classe</th>
                <th>Cognome Nome</th>
                <th style={{ width: 140 }}>Codice Fiscale</th>
                <th>Indirizzo</th>
              </tr></thead>
              <tbody>
                {list.map((a, i) => (
                  <tr key={a.Cod}>
                    <td style={{ color: "#888", fontSize: 11 }}>{i + 1}</td>
                    <td style={{ textAlign: "center", fontWeight: 700 }}>{a.settore || "-"}</td>
                    <td style={{ textAlign: "center", fontWeight: 700 }}>{a.classe || "-"}</td>
                    <td style={{ fontWeight: 600 }}>{a.Cog} {a.Nom}</td>
                    <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{a.CodFis || "—"}</td>
                    <td style={{ fontSize: 11 }}>{a.ResVia || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function EmailModal({ data, annoCorrente, onClose, onSent }) {
  const c = data.comune;
  // L'a.s. delle cedole è quello successivo all'anno scolastico in corso.
  // `annoCorrente` rappresenta l'anno di chiusura (es. 2026 = a.s. 2025/26),
  // quindi le cedole sono per a.s. (anno)/(anno+1−2000).
  const asLabel = `${annoCorrente}/${String((annoCorrente || 0) + 1).slice(-2)}`;
  const [form, setForm] = useState({
    destinatario: c.email || "",
    cc: "",
    oggetto: `Richiesta Cedole Librarie A.S. ${asLabel} - ${c.nome}`,
    corpo: `Spett.le COMUNE DI ${c.nome},\n\nIn allegato la documentazione da Voi richiesta al fine dell'ottenimento delle cedole librarie per l'A.S. ${asLabel} per gli alunni residenti nel Vostro comune.\n\nCordiali saluti,\nLa Segreteria — Istituto Canossa Diaz`,
  });
  const [allegati, setAllegati] = useState([]);   // File[]
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Aggiungi files mantenendo la selezione precedente (no replace).
  // Evita duplicati per nome+size.
  const addFiles = (newFiles) => {
    const incoming = Array.from(newFiles || []);
    setAllegati((prev) => {
      const key = (f) => `${f.name}::${f.size}`;
      const map = new Map(prev.map((f) => [key(f), f]));
      for (const f of incoming) map.set(key(f), f);
      return Array.from(map.values());
    });
    // reset input value per consentire una nuova selezione anche dello stesso file
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeFile = (idx) => setAllegati((prev) => prev.filter((_, i) => i !== idx));

  const send = async () => {
    if (!form.destinatario.trim()) { alert("Inserisci almeno un destinatario"); return; }
    if (allegati.length === 0) { if (!window.confirm("Stai per inviare senza allegati. Confermi?")) return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("destinatario", form.destinatario);
      fd.append("oggetto", form.oggetto);
      fd.append("corpo", form.corpo);
      if (form.cc) fd.append("cc", form.cc);
      allegati.forEach(f => fd.append("allegati", f));
      const r = await api.post(`/cedole/comuni/${encodeURIComponent(c.nome_norm)}/email`,
        fd, { params: { anno: annoCorrente }, headers: { "Content-Type": "multipart/form-data" } });
      alert(`✉️ Email inviata con successo a ${r.data.to.join(", ")} con ${r.data.allegati_count} allegat${r.data.allegati_count === 1 ? "o" : "i"} (id: ${r.data.id || "—"})`);
      onSent();
      onClose();
    } catch (e) {
      alert("Errore invio: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} data-testid="cedole-email-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(720px, 95vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", border: "2px solid #2e7d32" }}>
        <div style={{ background: "#2e7d32", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase" }}>Invio PEC al Comune</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>✉ {c.nome}</div>
            <div style={{ fontSize: 11, opacity: 0.9 }}>🔒 Mittente: canossadiaz@legalmail.it</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#1b5e20" }}>📧 Destinatario (separato da virgola):</label>
              <input type="text" value={form.destinatario} onChange={e => setForm(f => ({ ...f, destinatario: e.target.value }))}
                data-testid="cedole-mail-to"
                style={{ width: "100%", padding: 7, border: "1px solid #2e7d32", fontSize: 13, marginTop: 3 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>CC (opzionale):</label>
              <input type="text" value={form.cc} onChange={e => setForm(f => ({ ...f, cc: e.target.value }))}
                style={{ width: "100%", padding: 7, border: "1px solid #999", fontSize: 13, marginTop: 3 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#1b5e20" }}>📑 Oggetto:</label>
              <input type="text" value={form.oggetto} onChange={e => setForm(f => ({ ...f, oggetto: e.target.value }))}
                data-testid="cedole-mail-subject"
                style={{ width: "100%", padding: 7, border: "1px solid #2e7d32", fontSize: 13, marginTop: 3, fontWeight: 700 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#1b5e20" }}>✏ Corpo del messaggio:</label>
              <textarea value={form.corpo} onChange={e => setForm(f => ({ ...f, corpo: e.target.value }))}
                data-testid="cedole-mail-body"
                rows={8} style={{ width: "100%", padding: 7, border: "1px solid #2e7d32", fontSize: 13, marginTop: 3, resize: "vertical", fontFamily: "inherit" }} />
            </div>
            <div style={{ padding: 10, background: "#fff8e1", border: "1px dashed #ffa000", borderRadius: 3 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#e65100", display: "block", marginBottom: 4 }}>
                📎 Allegati (puoi aggiungerne anche più di uno, in più passaggi):
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png"
                  onChange={e => addFiles(e.target.files)}
                  data-testid="cedole-mail-allegati" style={{ fontSize: 12 }} />
                {allegati.length > 0 && (
                  <button type="button" onClick={() => setAllegati([])}
                    data-testid="cedole-mail-allegati-reset"
                    style={{ padding: "4px 10px", background: "white", color: "#c62828",
                             border: "1px solid #c62828", fontSize: 11, fontWeight: 700,
                             cursor: "pointer", borderRadius: 3 }}>
                    ✕ Svuota ({allegati.length})
                  </button>
                )}
              </div>
              {allegati.length > 0 && (
                <ul style={{ margin: "8px 0 0 0", padding: 0, listStyle: "none", fontSize: 11 }}>
                  {allegati.map((f, i) => (
                    <li key={`${f.name}-${i}`}
                        data-testid={`cedole-mail-allegato-${i}`}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0",
                                 borderBottom: i < allegati.length - 1 ? "1px dashed #ffd54f" : "none" }}>
                      <span style={{ color: "#1b5e20", fontWeight: 700 }}>📎 {f.name}</span>
                      <span style={{ color: "#666" }}>({Math.round(f.size / 1024)} KB)</span>
                      <button type="button" onClick={() => removeFile(i)}
                        title="Rimuovi questo allegato"
                        data-testid={`cedole-mail-allegato-del-${i}`}
                        style={{ marginLeft: "auto", padding: "1px 6px", background: "white",
                                 color: "#c62828", border: "1px solid #ef9a9a",
                                 fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>✕</button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ fontSize: 10, color: "#888", marginTop: 6, fontStyle: "italic" }}>
                Premi il selettore più volte per aggiungere file da cartelle diverse — la lista si accumula.
                Dimensione totale max: 20 MB. Duplicati (stesso nome + dimensione) vengono ignorati.
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose}
            style={{ padding: "8px 16px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={send} disabled={busy} data-testid="cedole-mail-send"
            style={{ padding: "8px 22px", background: busy ? "#9e9e9e" : "#2e7d32", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", letterSpacing: 0.4 }}>
            {busy ? "Invio…" : "✉ Invia mail"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnagModal({ data, onClose, onSaved }) {
  const c = data.comune;
  const [form, setForm] = useState({
    email: c.email || "",
    pec: c.pec || "",
    indirizzo: c.indirizzo || "",
    telefono: c.telefono || "",
    modalita: c.modalita || "nulla",
    avanzamento: c.avanzamento || "da_fare",
    note: c.note || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/cedole/comuni/${encodeURIComponent(c.nome_norm)}`, { ...form, nome: c.nome });
      onSaved(); onClose();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} data-testid="cedole-anag-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(560px, 95vw)", display: "flex", flexDirection: "column", border: "2px solid #5d4037" }}>
        <div style={{ background: "#5d4037", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>✏ Anagrafica — {c.nome}</div>
          <button onClick={onClose} style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 10 }}>
          {[
            ["email", "📧 Email"],
            ["pec", "🔒 PEC"],
            ["indirizzo", "📍 Indirizzo"],
            ["telefono", "☎ Telefono"],
          ].map(([k, lbl]) => (
            <div key={k}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#5d4037" }}>{lbl}</label>
              <input type="text" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                data-testid={`cedole-anag-${k}`}
                style={{ width: "100%", padding: 7, border: "1px solid #5d4037", fontSize: 13, marginTop: 3 }} />
            </div>
          ))}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#5d4037" }}>📡 Modalità invio</label>
            <select value={form.modalita} onChange={e => setForm(f => ({ ...f, modalita: e.target.value }))}
              data-testid="cedole-anag-modalita"
              style={{ width: "100%", padding: 7, border: "1px solid #5d4037", fontSize: 13, marginTop: 3, background: "white" }}>
              <option value="nulla">— Nulla (non ancora deciso) —</option>
              <option value="digitale">💻 Digitale (email)</option>
              <option value="cartacea">📄 Cartacea (posta)</option>
              <option value="codice">📟 Codice</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#5d4037" }}>📌 Avanzamento</label>
            <select value={form.avanzamento} onChange={e => setForm(f => ({ ...f, avanzamento: e.target.value }))}
              data-testid="cedole-anag-avanzamento"
              style={{ width: "100%", padding: 7, border: "1px solid #5d4037", fontSize: 13, marginTop: 3, background: "white" }}>
              <option value="da_fare">⏳ Da fare</option>
              <option value="inviata_documentazione">✅ Inviata documentazione</option>
              <option value="ricevuto_cedole">📬 Ricevuto cedole</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#5d4037" }}>📝 Note</label>
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              rows={3} style={{ width: "100%", padding: 7, border: "1px solid #5d4037", fontSize: 13, marginTop: 3, resize: "vertical", fontFamily: "inherit" }} />
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose}
            style={{ padding: "8px 16px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={save} disabled={busy} data-testid="cedole-anag-save"
            style={{ padding: "8px 22px", background: busy ? "#9e9e9e" : "#5d4037", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Salvataggio…" : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Modal Note (per singolo comune) ─────────── */
function NoteModal({ data, onClose, onSaved }) {
  const [text, setText] = useState(data.note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/cedole/comuni/${encodeURIComponent(data.nome_norm)}`, {
        nome: data.nome, email: data.email, pec: data.pec, indirizzo: data.indirizzo,
        telefono: data.telefono, modalita: data.modalita,
        avanzamento: data.avanzamento || "da_fare", note: text,
      });
      onSaved(text);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio note");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="cedole-note-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: 6, maxWidth: 600, width: "100%", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "12px 18px", background: "#f57f17", color: "white", borderRadius: "6px 6px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>📝 Note — {data.nome}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#5d4037" }}>Note generiche (testo libero)</label>
          <textarea data-testid="cedole-note-textarea"
            value={text} onChange={e => setText(e.target.value)}
            rows={10}
            placeholder="Scrivi qui le note relative a questo Comune (es. referente, scadenze, accordi presi…)"
            style={{ width: "100%", padding: 10, border: "1px solid #ffa000", fontSize: 13, marginTop: 4, fontFamily: "inherit", resize: "vertical" }} />
          {err && <div style={{ marginTop: 8, padding: 8, background: "#ffebee", color: "#c62828", fontSize: 11 }}>{err}</div>}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding: "7px 14px", background: "#757575", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            Annulla
          </button>
          <button onClick={save} disabled={busy}
            data-testid="cedole-note-save"
            style={{ padding: "7px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            {busy ? "Salvataggio…" : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
